import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  getGamesList,
  handlePaginateGames,
  handleSelectAllGames,
  handleSelectItemGames,
  updateFreeSpinsGamesBunch,
} from "store/liveOps/bonuses/slice";
import { gamesListLimit, gamesListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { FilterItemTypes } from "configs/types";

import ToggleBtn from "components/buttons/ToggleBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Filter from "components/global/Filter/Filter";
import editIcon from "assets/icons/edit-icon.svg";
import inactiveIcon from "assets/icons/inactive.svg";
import listIcon from "assets/icons/list-icon.svg";
import gridIcon from "assets/icons/grid-icon.svg";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import Pagination from "components/Pagination";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import ImageWithFallback from "helpers/ImageWithFallback";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import ROUTES from "router/routes";
import {
  NotificationTypes,
  showNotification,
} from "store/notifications/slice";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";
import BooleanLabel from "components/common/BooleanLabel/BooleanLabel";

const isEnabledGameFlag = (value: unknown) =>
  ["true", "1"].includes(String(value).toLowerCase());

const GamesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const permissions = usePermissionHelper();

  const [order, setOrder] = useState<{
    title: string;
    providerTitle: string;
  }>({
    title: "none",
    providerTitle: "none",
  });
  const [activeView, setView] = useState<string>("table");
  const [currentLimit, setCurrentLimit] = useState(gamesListLimit);
  const [search, setSearch] = useState<string>("");
  const [providersSelected, setProvidersSelected] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [providers, setProviders] = useState<
    { value: string | number; label: string }[]
  >([]);
  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("addToWagerList"),
      value: "addToWagerList",
    },
    {
      label: t("hide"),
      value: "hide",
    },
    {
      label: t("disable"),
      value: "disable",
    },
  ];

  const gamesList = useAppSelector((store) => store.bonuses.gamesList);
  const isListFetched = useAppSelector(
    (store) => store.bonuses.isListGamesFetched,
  );
  const totalItems = useAppSelector((store) => store.bonuses.totalItemsGames);
  const totalPages = useAppSelector((store) => store.bonuses.totalPagesGames);
  const activePage = useAppSelector((store) => store.bonuses.activePageGames);
  const processingGameItemId = useAppSelector(
    (store) => store.bonuses.processingGameItemId,
  );
  const selectedItems = useAppSelector(
    (store) => store.bonuses.selectedItemGames,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'bonuses',
    isListFetched,
    field: 'isListGamesFetched'
  });

  const getProviders = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.providersList}?limit=500&offset=0`,
      );
      const array =
        result.data.items?.map((el: any) => {
          return {
            value: el.id,
            label: el.title,
          };
        }) ?? [];
      setProviders(array);
    } catch (error) {
      console.log(error, "error");
    }
  };
  useEffect(() => {
    if (!permissions?.read) return;
    getProviders();
  }, [permissions?.read]);

  useEffect(() => {
    if (!permissions?.read) return;

    if (!isListFetched) {
      dispatch(
        getGamesList({
          limit: currentLimit,
          offset: activePage - 1,
          providerIds: providersSelected.map((el) => el.value as number),
          gameTitle: search === "" ? undefined : search,
          order,
        }),
      );
    }
  }, [permissions?.read, isListFetched, currentLimit]);

  const handleAllClick = () => {
    if (!permissions?.update) return;
    dispatch(handleSelectAllGames());
  };
  const handleItemClick = (id: number) => {
    if (!permissions?.update) return;
    dispatch(handleSelectItemGames(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginateGames(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginateGames(activePage + 1));
    }
  };

  const handleActionClick = async (e: string) => {
    if (!permissions?.update) return;

    if (
      (e === "activate" || e === "disable") &&
      selectedItems.length > 0
    ) {
      const result = await dispatch(
        updateFreeSpinsGamesBunch({
          gameIds: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );

      dispatch(
        showNotification({
          title: result.payload ? t("success") : t("error"),
          message: result.payload ? t("actionSuccess") : t("actionError"),
          type: result.payload
            ? NotificationTypes.success
            : NotificationTypes.error,
        }),
      );
    }

    if (e === "addToWagerList" && selectedItems.length > 0) {
      dispatch(
        openModal({
          activeModal: ModalTypes.addGamesToWager,
        }),
      );
    }
  };

  const handleOpenEditModal = (id: number) => {
    if (!permissions?.update) return;

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.editGame}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.editGame }));
  };

  const handleInactiveClick = () => {
    dispatch(
      showNotification({
        title: t("warning"),
        message: t("noEditPermissionContactAdmin"),
        type: NotificationTypes.warning,
      }),
    );
  };

  const handleApply = () => {
    dispatch(handlePaginateGames(1));
  };
  const handleClearFilters = () => {
    setSearch("");
    setProvidersSelected([]);
    handleApply();
  };
  const handleSearch = (e: string) => {
    setSearch(e);
  };
  const handleApplyProviders = (
    e: { value: string | number; label: string }[],
    isDelete?: boolean,
  ) => {
    setProvidersSelected(isDelete ? e : [...providersSelected, ...e]);
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginateGames(1));
  };

  if (permissions && !permissions.read) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <Filter
        className="free-spins-filter"
        handleClear={handleClearFilters}
        handleApply={handleApply}
        filterItems={[
          {
            type: FilterItemTypes.input,
            title: t("title"),
            boxTitle: t("title"),
            icon: gridIcon,
            inputValue: search,
            setInputValue: handleSearch,
          },
          {
            type: FilterItemTypes.multiselect,
            title: t("providers"),
            boxTitle: t("filterProvider"),
            icon: gridIcon,
            itemsArray: providers,
            activeItems: providersSelected,
            handleApply: handleApplyProviders,
            disabled: providers.length === 0,
          },
        ]}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box"></div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box">
            <ToggleBtn
              handleClick={(v) => setView(v)}
              activeValue={activeView}
              disabled={gamesList.length === 0}
              icons={[
                {
                  url: listIcon,
                  value: "table",
                },
                {
                  url: gridIcon,
                  value: "grid",
                },
              ]}
            />
            {permissions?.update && (
              <DefaultDropdown
                className="default-dropdown--sentence-case"
                onSelect={(e) => {
                  handleActionClick(e as string);
                }}
                placeholder={t("chooseAction")}
                options={options}
                isSmall
                disabled={selectedItems.length === 0}
              />
            )}
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={8} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div
              className={`game-table-wrap ${activeView === "table" ? "" : "grid"}`}
            >
              {gamesList.length === 0 && (
                <EmptyState message={t("noFreeSpinsFound")} />
              )}

              {gamesList.length > 0 && (
                <>
                  {activeView === "table" ? (
                    <Table
                      data={gamesList}
                      order={order}
                      idField="id"
                      activeRowId={searchParams.get("entityId")}
                      processingId={processingGameItemId}
                      selectedItems={selectedItems}
                      onAllSelect={handleAllClick}
                      onSelect={(id) => handleItemClick(id)}
                      onEdit={(id) => handleOpenEditModal(id)}
                      hasEdit={permissions?.update}
                      hasInactive={permissions?.read && !permissions?.update}
                      onInactive={handleInactiveClick}
                      hasCheckbox={permissions?.update}
                      onSort={(id) => (id ? handleOrderClick(id) : {})}
                      hasDelete={false}
                      customColumns={(row) => ({
                        title: (
                          <div className="copyable-title-cell">
                            <CopyToClipboard text={row.title} />
                            <span>{row.title}</span>
                          </div>
                        ),
                        hasDemo: (
                          <BooleanLabel
                            value={isEnabledGameFlag(row.hasDemo)}
                          />
                        ),
                      })}
                      columns={{
                        hiddenColumns: [
                          "images",
                          "isMobile",
                          "isHidden",
                          "isDeactivated",
                          "hasFreeSpins",
                        ],
                        columnOrder: [
                          "id",
                          "image",
                          "title",
                          "slug",
                          "provider",
                          "hasDemo",
                          "type",
                          "devices",
                          "isActive",
                        ],
                      }}
                    />
                  ) : (
                    <div className="game-grid">
                      {gamesList.map((el) => (
                        <div className="game-item" key={el.id}>
                          <div className="game-item-img">
                            <ImageWithFallback
                              src={el.image as string}
                              alt={el.title}
                              className="game-item-img__image"
                            />
                          </div>
                          <div className="game-item-content">
                            <div className="game-item-content--title">
                              {el.title}
                            </div>
                            <div className="game-item-content--actions">
                              {permissions?.update && (
                                <div
                                  onClick={() => handleItemClick(el.id)}
                                  className={`def-check-control ${
                                    selectedItems.includes(el.id)
                                      ? "checked"
                                      : ""
                                  }`}
                                />
                              )}
                              <div className="provider">
                                {el.provider.title}
                              </div>
                              {permissions?.update ? (
                                <div
                                  className="option"
                                  onClick={() => handleOpenEditModal(el.id)}
                                >
                                  <img src={editIcon} alt="Edit game" />
                                </div>
                              ) : (
                                <div
                                  className="option inactive"
                                  onClick={handleInactiveClick}
                                >
                                  <img
                                    src={inactiveIcon}
                                    alt="Action unavailable"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {gamesList.length > 0 && (
              <Pagination
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                limitsArray={gamesListLimits}
                handleLimitClick={(limit) => {
                  setCurrentLimit(limit);
                  dispatch(handlePaginateGames(1));
                }}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GamesList;
